/**
 * Weather service — historical + estimated track conditions.
 * -------------------------------------------------------------------------
 * Source priority:
 *   1. OpenF1 /weather (2023+) — real track-sensor data
 *   2. Open-Meteo archive API — free historical air temp + cloud/rain
 *      combined with an estimation formula for track temperature
 *
 * Open-Meteo: https://open-meteo.com/
 *   - No API key, no rate limit worth worrying about
 *   - Historical hourly data from 1940 to present
 *   - We query by circuit GPS coordinates + race date
 *
 * Track temperature estimation:
 *   Based on the formula F1 teams use as a pre-session baseline.
 *   Accuracy: ±3-5°C of real sensor readings.
 */

const OPENF1_BASE = 'https://api.openf1.org/v1';
const METEO_BASE = 'https://archive-api.open-meteo.com/v1/archive';

/**
 * Circuit GPS coordinates — needed for Open-Meteo queries.
 * Source: official FIA circuit data sheets.
 */
const CIRCUIT_COORDS = {
  // GPS coordinates from official FIA circuit data sheets
  'Melbourne': { lat: -37.8497, lon: 144.9680 },
  'Sakhir': { lat: 26.0325, lon: 50.5106 },
  'Hanoï': { lat: 21.0045, lon: 105.7656 },
  'Hanoi': { lat: 21.0045, lon: 105.7656 },
  'Anting': { lat: 31.3389, lon: 121.2197 },
  'Shanghai': { lat: 31.3389, lon: 121.2197 },
  'Zandvoort': { lat: 52.3888, lon: 4.5409 },
  'Barcelona': { lat: 41.57, lon: 2.2611 },
  'Monte-Carlo': { lat: 43.7347, lon: 7.4206 },
  'Monte Carlo': { lat: 43.7347, lon: 7.4206 },
  'Monaco': { lat: 43.7347, lon: 7.4206 },
  'Bakou': { lat: 40.3725, lon: 49.8533 },
  'Baku': { lat: 40.3725, lon: 49.8533 },
  'Montréal': { lat: 45.5017, lon: -73.5228 },
  'Montreal': { lat: 45.5017, lon: -73.5228 },
  'Le Castellet': { lat: 43.2506, lon: 5.7917 },
  'Spielberg': { lat: 47.2197, lon: 14.7647 },
  'Silverstone Northamptonshire': { lat: 52.0786, lon: -1.0169 },
  'Silverstone': { lat: 52.0786, lon: -1.0169 },
  'Hockenheim': { lat: 49.3278, lon: 8.5656 },
  'Mogyoród': { lat: 47.5789, lon: 19.2486 },
  'Budapest': { lat: 47.5789, lon: 19.2486 },
  'Francorchamps': { lat: 50.4372, lon: 5.9714 },
  'Spa-Francorchamps': { lat: 50.4372, lon: 5.9714 },
  'Monza': { lat: 45.6156, lon: 9.2811 },
  'Singapour': { lat: 1.2914, lon: 103.8636 },
  'Singapore': { lat: 1.2914, lon: 103.8636 },
  'Marina Bay': { lat: 1.2914, lon: 103.8636 },
  'Sochi': { lat: 43.4057, lon: 39.9578 },
  'Suzuka': { lat: 34.8431, lon: 136.5411 },
  'Elroy': { lat: 30.1328, lon: -97.6411 },
  'Austin': { lat: 30.1328, lon: -97.6411 },
  'Mexico': { lat: 19.4042, lon: -99.0907 },
  'Mexico City': { lat: 19.4042, lon: -99.0907 },
  'São Paulo': { lat: -23.7014, lon: -46.6969 },
  'Yas Marina, Abou Dabi': { lat: 24.4672, lon: 54.6031 },
  'Yas Marina': { lat: 24.4672, lon: 54.6031 },
  // Jolpica's locality for the season finale is "Abu Dhabi", which matched
  // neither key above (not even fuzzily — "abou dabi" != "abu dhabi"), so the
  // last race of EVERY season silently rendered weather as "-".
  'Abu Dhabi': { lat: 24.4672, lon: 54.6031 },
  'Algarve': { lat: 37.227, lon: -8.6267 },
  'Portimão': { lat: 37.227, lon: -8.6267 },
  'Portimao': { lat: 37.227, lon: -8.6267 },
  'Mugello': { lat: 43.9975, lon: 11.3719 },
  'Nürburgring': { lat: 50.3356, lon: 6.9475 },
  'Imola': { lat: 44.3439, lon: 11.7167 },
  'Istanbul': { lat: 40.9517, lon: 29.405 },
  'Djeddah': { lat: 21.6319, lon: 39.1044 },
  'Jeddah': { lat: 21.6319, lon: 39.1044 },
  'Losail': { lat: 25.49, lon: 51.4542 },
  'Lusail': { lat: 25.49, lon: 51.4542 },
  'Miami': { lat: 25.9581, lon: -80.2389 },
  'Miami Gardens': { lat: 25.9581, lon: -80.2389 },
  'Las Vegas': { lat: 36.1147, lon: -115.1728 },
  'Madrid': { lat: 40.4168, lon: -3.7038 },
  'Sepang': { lat: 2.7606, lon: 101.7383 },
  'Yeongam': { lat: 34.7333, lon: 126.4167 },
  'Valencia': { lat: 39.4583, lon: -0.3317 },
  'New Delhi': { lat: 28.3487, lon: 77.5331 },
  // Jolpica localities absent from the original (API-Sports-derived) map.
  // Within the 2000+ UI window these covered real races: Indianapolis
  // (US GP 2000-07), Kuala Lumpur (Malaysian GP, Jolpica's name for Sepang),
  // Magny Cours (French GP 2000-08).
  'Kuala Lumpur': { lat: 2.7606, lon: 101.7383 },
  'Indianapolis': { lat: 39.7950, lon: -86.2347 },
  'Magny Cours': { lat: 46.8642, lon: 3.1636 },
  'Magny-Cours': { lat: 46.8642, lon: 3.1636 },
  'Montmeló': { lat: 41.57, lon: 2.2611 },
  'Montmelo': { lat: 41.57, lon: 2.2611 },
  'Nurburg': { lat: 50.3356, lon: 6.9475 },
  'Spa': { lat: 50.4372, lon: 5.9714 },
  'Sao Paulo': { lat: -23.7014, lon: -46.6969 },
  'Uttar Pradesh': { lat: 28.3487, lon: 77.5331 },  // Buddh Int'l (Indian GP 2011-13)
  'Oyama': { lat: 35.3717, lon: 138.9267 },          // Fuji Speedway (Japanese GP 2007-08)
};

/**
 * fetchWeather — gets weather data for a race session.
 *
 * @param {number|null} openF1SessionKey — if available, tries OpenF1 first
 * @param {string} location — circuit city/location name for coord lookup
 * @param {string} dateStart — ISO 8601 race start time
 * @param {object} options
 * @returns {Promise<{ air_temp_c, track_temp_c, humidity_pct, conditions, estimated }>}
 */
export async function fetchWeather(openF1SessionKey, location, dateStart, { signal } = {}) {
  // Try OpenF1 first (real sensor data).
  if (openF1SessionKey) {
    try {
      const data = await fetchOpenF1Weather(openF1SessionKey, { signal });
      if (data) return { ...data, estimated: false };
    } catch {
      // Sensor data is a best-effort enrichment — fall through to estimation.
    }
  }

  // Fallback: Open-Meteo historical + track temp estimation.
  try {
    const data = await fetchOpenMeteoWeather(location, dateStart, { signal });
    if (data) return { ...data, estimated: true };
  } catch {
    // Weather is never load-bearing; the caller renders "-" placeholders.
  }

  return { air_temp_c: '-', track_temp_c: '-', humidity_pct: '-', conditions: '-', estimated: false };
}

/**
 * OpenF1 weather — real track-sensor data (2023+).
 */
async function fetchOpenF1Weather(sessionKey, { signal }) {
  const res = await fetch(`${OPENF1_BASE}/weather?session_key=${sessionKey}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const data = await res.json();
  if (!data || data.length === 0) return null;

  const last = data[data.length - 1];
  return {
    air_temp_c: Math.round(last.air_temperature),
    track_temp_c: Math.round(last.track_temperature),
    humidity_pct: Math.round(last.humidity),
    conditions: last.rainfall > 0 ? 'Wet' : 'Dry',
  };
}

/**
 * Open-Meteo historical weather + track temperature estimation.
 * Free API, no key required, works for any date back to 1940.
 */
async function fetchOpenMeteoWeather(location, dateStart, { signal }) {
  if (!location || !dateStart) return null;

  const coords = findCoords(location);
  if (!coords) return null;

  const raceDate = new Date(dateStart);
  const dateStr = raceDate.toISOString().slice(0, 10);
  const raceHour = raceDate.getUTCHours();

  const url = `${METEO_BASE}?latitude=${coords.lat}&longitude=${coords.lon}&start_date=${dateStr}&end_date=${dateStr}&hourly=temperature_2m,relativehumidity_2m,cloudcover,rain&timezone=UTC`;

  const res = await fetch(url, { signal });
  if (!res.ok) return null;
  const data = await res.json();

  if (!data?.hourly?.temperature_2m) return null;

  // Use the hour closest to race start.
  const airTemp = data.hourly.temperature_2m[raceHour] ?? data.hourly.temperature_2m[14];
  const humidity = data.hourly.relativehumidity_2m?.[raceHour] ?? '-';
  const cloudCover = data.hourly.cloudcover?.[raceHour] ?? 0;
  const rain = data.hourly.rain?.[raceHour] ?? 0;

  // Determine conditions.
  let conditions;
  if (rain > 0.5) conditions = 'Wet';
  else if (cloudCover > 75) conditions = 'Overcast';
  else if (cloudCover > 40) conditions = 'Partly Cloudy';
  else conditions = 'Clear';

  // Estimate track temperature.
  const trackTemp = estimateTrackTemp(airTemp, cloudCover, rain);

  return {
    air_temp_c: Math.round(airTemp),
    track_temp_c: Math.round(trackTemp),
    humidity_pct: typeof humidity === 'number' ? Math.round(humidity) : '-',
    conditions,
  };
}

/**
 * Track temperature estimation formula.
 *
 * Based on empirical F1 data:
 *   - Clear/sunny: track = air + 15-20°C
 *   - Partly cloudy: track = air + 10-14°C
 *   - Overcast: track = air + 5-9°C
 *   - Wet/rain: track = air + 2-4°C
 */
function estimateTrackTemp(airTemp, cloudCover, rain) {
  let offset;
  if (rain > 0.5) {
    offset = 3;
  } else if (cloudCover > 75) {
    offset = 7;
  } else if (cloudCover > 40) {
    offset = 12;
  } else {
    offset = 17;
  }
  return airTemp + offset;
}

/**
 * Find GPS coordinates for a circuit location name.
 * Tries exact match first, then fuzzy substring match.
 */
function findCoords(location) {
  if (CIRCUIT_COORDS[location]) return CIRCUIT_COORDS[location];

  // Fuzzy match — fallback if exact match fails.
  const lower = location.toLowerCase();
  for (const [key, coords] of Object.entries(CIRCUIT_COORDS)) {
    if (key.toLowerCase().includes(lower) || lower.includes(key.toLowerCase())) {
      return coords;
    }
  }
  return null;
}
